import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select } from "./ui/select";
import { useToast } from "../hooks/use-toast";

const GestionHorasProfesor = () => {
  const { user } = useContext(AuthContext);
  const { toast } = useToast();
  const [horas, setHoras] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profesores, setProfesores] = useState([]);
  const [clases, setClases] = useState([]);
  
  // Filtros
  const [filtroProfesor, setFiltroProfesor] = useState('');
  const [filtroClase, setFiltroClase] = useState('');
  const [filtroFechaInicio, setFiltroFechaInicio] = useState('');
  const [filtroFechaFin, setFiltroFechaFin] = useState('');
  const [vistaActual, setVistaActual] = useState('lista'); // 'lista' o 'resumen'

  const API = window.location.origin;

  useEffect(() => {
    if (user) {
      cargarDatos();
      cargarProfesores();
      cargarClases();
    }
  }, [user]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Cargar lista de horas
      const horasResponse = await axios.get(`${API}/api/horas-profesor`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          profesor_id: filtroProfesor || undefined,
          clase_id: filtroClase || undefined,
          fecha_inicio: filtroFechaInicio || undefined,
          fecha_fin: filtroFechaFin || undefined
        }
      });
      
      setHoras(horasResponse.data.horas || []);
      
      // Cargar resumen
      const resumenResponse = await axios.get(`${API}/api/horas-profesor/reporte`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResumen(resumenResponse.data);
    } catch (error) {
      console.error('Error al cargar horas:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las horas registradas",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const cargarProfesores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const profesList = response.data.filter(u => u.rol === 'profesor');
      setProfesores(profesList);
    } catch (error) {
      console.error('Error al cargar profesores:', error);
    }
  };

  const cargarClases = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/clases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setClases(response.data || []);
    } catch (error) {
      console.error('Error al cargar clases:', error);
    }
  };

  const aplicarFiltros = () => {
    cargarDatos();
  };

  const limpiarFiltros = () => {
    setFiltroProfesor('');
    setFiltroClase('');
    setFiltroFechaInicio('');
    setFiltroFechaFin('');
    setTimeout(cargarDatos, 100);
  };

  const exportarExcel = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/horas-profesor/exportar`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          profesor_id: filtroProfesor || undefined,
          fecha_inicio: filtroFechaInicio || undefined,
          fecha_fin: filtroFechaFin || undefined
        },
        responseType: 'blob'
      });

      // Crear URL del blob y descargar
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const fecha = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `Horas_Profesores_${fecha}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Éxito",
        description: "Archivo Excel descargado correctamente",
        variant: "success"
      });
    } catch (error) {
      console.error('Error al exportar:', error);
      toast({
        title: "Error",
        description: "No se pudo exportar el archivo Excel",
        variant: "destructive"
      });
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-UY', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          <i className="fas fa-clock mr-2 text-blue-600"></i>
          Gestión de Horas de Profesores
        </h1>
        <p className="text-gray-600">
          Registro automático de horas cuando el primer estudiante marca asistencia
        </p>
      </div>

      {/* Filtros */}
      <Card className="mb-6 p-4">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <i className="fas fa-filter mr-2 text-blue-600"></i>
          Filtros
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Filtro Profesor - Solo para admin */}
          {user?.rol === 'admin' && (
            <div>
              <label className="block text-sm font-medium mb-1">Profesor</label>
              <select
                value={filtroProfesor}
                onChange={(e) => setFiltroProfesor(e.target.value)}
                className="w-full p-2 border rounded-lg"
              >
                <option value="">Todos los profesores</option>
                {profesores.map(prof => (
                  <option key={prof.id} value={prof.id}>
                    {prof.nombre} {prof.apellido}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Filtro Clase */}
          <div>
            <label className="block text-sm font-medium mb-1">Clase</label>
            <select
              value={filtroClase}
              onChange={(e) => setFiltroClase(e.target.value)}
              className="w-full p-2 border rounded-lg"
            >
              <option value="">Todas las clases</option>
              {clases.map(clase => (
                <option key={clase.id} value={clase.id}>
                  {clase.titulo}
                </option>
              ))}
            </select>
          </div>

          {/* Fecha Inicio */}
          <div>
            <label className="block text-sm font-medium mb-1">Fecha Inicio</label>
            <Input
              type="date"
              value={filtroFechaInicio}
              onChange={(e) => setFiltroFechaInicio(e.target.value)}
            />
          </div>

          {/* Fecha Fin */}
          <div>
            <label className="block text-sm font-medium mb-1">Fecha Fin</label>
            <Input
              type="date"
              value={filtroFechaFin}
              onChange={(e) => setFiltroFechaFin(e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={aplicarFiltros} className="bg-blue-600 hover:bg-blue-700">
            <i className="fas fa-search mr-2"></i>
            Aplicar Filtros
          </Button>
          <Button onClick={limpiarFiltros} variant="outline">
            <i className="fas fa-times mr-2"></i>
            Limpiar
          </Button>
          <Button onClick={exportarExcel} className="bg-green-600 hover:bg-green-700 ml-auto">
            <i className="fas fa-file-excel mr-2"></i>
            Exportar Excel
          </Button>
        </div>
      </Card>

      {/* Tabs para cambiar vista */}
      <div className="flex gap-2 mb-4">
        <Button
          onClick={() => setVistaActual('lista')}
          variant={vistaActual === 'lista' ? 'default' : 'outline'}
        >
          <i className="fas fa-list mr-2"></i>
          Lista Detallada
        </Button>
        <Button
          onClick={() => setVistaActual('resumen')}
          variant={vistaActual === 'resumen' ? 'default' : 'outline'}
        >
          <i className="fas fa-chart-bar mr-2"></i>
          Resumen por Profesor
        </Button>
      </div>

      {/* Vista Lista Detallada */}
      {vistaActual === 'lista' && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">
              Horas Registradas ({horas.length})
            </h2>
          </div>

          {horas.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <i className="fas fa-calendar-times text-6xl mb-4"></i>
              <p className="text-lg">No hay horas registradas con los filtros seleccionados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Profesor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Clase
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Horas
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Primer Estudiante
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {horas.map((hora) => (
                    <tr key={hora.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {formatearFecha(hora.fecha_registro)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {hora.profesor_nombre}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {hora.clase_titulo}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          {hora.horas} hora{hora.horas !== 1 ? 's' : ''}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <i className="fas fa-user-graduate mr-1"></i>
                        {hora.primer_estudiante_nombre}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Vista Resumen por Profesor */}
      {vistaActual === 'resumen' && resumen && (
        <div className="space-y-4">
          {resumen.resumen.length === 0 ? (
            <Card className="p-8 text-center text-gray-500">
              <i className="fas fa-chart-line text-6xl mb-4"></i>
              <p className="text-lg">No hay datos de resumen disponibles</p>
            </Card>
          ) : (
            resumen.resumen.map((profesor) => (
              <Card key={profesor._id} className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800">
                      <i className="fas fa-chalkboard-teacher mr-2 text-blue-600"></i>
                      {profesor.profesor_nombre}
                    </h3>
                  </div>
                  <div className="flex gap-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600">
                        {profesor.total_horas}
                      </div>
                      <div className="text-sm text-gray-600">Total Horas</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600">
                        {profesor.total_clases}
                      </div>
                      <div className="text-sm text-gray-600">Clases Dadas</div>
                    </div>
                  </div>
                </div>

                {/* Lista de clases */}
                <div className="mt-4 border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Detalle de Clases:</h4>
                  <div className="space-y-2">
                    {profesor.clases.map((clase, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <div className="font-medium text-gray-800">{clase.clase_titulo}</div>
                          <div className="text-sm text-gray-600">
                            {formatearFecha(clase.fecha_registro)} • Primer estudiante: {clase.primer_estudiante_nombre}
                          </div>
                        </div>
                        <div className="ml-4">
                          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                            {clase.horas} hora{clase.horas !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default GestionHorasProfesor;
